"use client";
import { useChatbotsTrial } from "@zapier/client";

import {
  Button,
  Link,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import { useTranslations } from "@zapier/i18n";
import {
  MONOLITH_BASE_URL,
  getChatbotsIndexUrl,
  useNotification,
} from "@zapier/utils";
import { Icon } from "@zapier/zinnia-icons";
import styles from "./ChatbotUpgradeModal.module.css";

interface ChatbotUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatbotName?: string;
}

const getUpgradeUrl = () => {
  const redirectUrl = encodeURIComponent(
    `${MONOLITH_BASE_URL}${getChatbotsIndexUrl()}`,
  );
  return `${MONOLITH_BASE_URL}/app/planbuilder/add-ons?chatbots=chatbots-advanced&redirectUrl=${redirectUrl}`;
};

export function ChatbotUpgradeModal({
  isOpen,
  onClose,
  chatbotName,
}: ChatbotUpgradeModalProps) {
  const t = useTranslations("chatbot-upgrade-modal");
  const { createNotification } = useNotification();
  const { trial, extendTrial, isExtendingTrial } = useChatbotsTrial();

  const upgradeUrl = getUpgradeUrl();
  const canExtendTrial = trial && trial.extendedAt === null;

  const handleExtendTrial = () => {
    if (!trial) return;
    extendTrial(
      { trialId: trial.id },
      {
        onError: () => {
          createNotification({
            message: t("notifications.extend-error"),
            type: "error",
          });
        },
      },
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={handleOpenChange}>
      <ModalDialogHeader textAlign="start">
        <div className={styles.headerContent}>
          <Icon color="BrandOrange" name="navAIChatbot" size={24} />
          <span>
            {chatbotName ? t("title-with-name", { chatbotName }) : t("title")}
          </span>
        </div>
      </ModalDialogHeader>
      <ModalDialogBody textAlign="start">
        <Text color="TextWeaker">{t("description")}</Text>
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button variant="secondary" onPress={onClose}>
          {t("buttons.cancel")}
        </Button>
        {canExtendTrial && (
          <Button
            variant="tertiary"
            isDisabled={isExtendingTrial}
            isPending={isExtendingTrial}
            onPress={handleExtendTrial}
          >
            {t("buttons.extend-trial")}
          </Button>
        )}
        <Link variant="brand" href={upgradeUrl}>
          {t("buttons.upgrade")}
        </Link>
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
}
